/* ============================================================
   CART DRAWER — live AJAX wiring, CART MODEL V3 ("Option C —
   one thing at a time"). Faithful port of the locked design
   prototype/cart-drawer-v3-steps-options.html (Option C): the
   cCard renderer + whoPanel (with Edit/Done name-deletion),
   makeEngine model, footHTML (compact waiver), and the C-specific
   atoms — onto live Shopify cart data.

   INVARIANT (carried from V2, unchanged): one registration = one
   cart LINE = qty 1 = one date variant + one `Dancer` property
   (a single name, never comma-joined). The PDP date-picker adds
   one line per picked date (variant), qty 1, no Dancer prop. So a
   live line maps cleanly to a "registration":
       registration = { cls: product_title, date: variant_title,
                        dancer: <Dancer prop, "" if unassigned>,
                        key: line.key, vid: variant_id,
                        price: line.final_price, url, image }

   V3 DISPLAY (differs from V2): ONE CARD PER PRODUCT (class). A card
   holds ALL the product's lines (date variants). `perDate[date]` is
   the list of Dancer names on that date's lines (a blank-Dancer line
   contributes no name but the date still exists as a held spot).
   Three card states:
     - UNASSIGNED — no dancers on the card (every line blank): orange
       border, count-expander peek + "Assign a dancer to this class"
       panel LAST.
     - SETTLED COLLAPSED — every line has a Dancer: quiet summary row.
     - SETTLED EXPANDED — head + "Who's dancing?" panel + "Fine-tune
       classes" → per-date rows.

   ACTIONS → live cart writes (mapping locked by the build prompt):
   - Assign first dancer (unassigned, chip tap or new name) → change.js
     every blank line of the product → Dancer: name.
   - Add another dancer card-wide (off / mixed chip) → for each date
     they're NOT on, add.js that variant with their Dancer prop.
   - Remove a dancer from a date (row chip confirm / unpress across all):
     if other dancers hold that date → remove their line (change.js
     qty 0); if SOLE holder → blank the Dancer to "" (LESSONS 108) so
     the paid spot stays and the card flips unassigned.
   - Row "+" pick → add.js that one variant with the Dancer.
   - Date row × → remove every line of that variant for the product;
     last date removed → card gone.
   - Delete a saved name → cascade the per-date removal rule across all
     cards + drop from localStorage (cs-dancers).

   API QUIRK (LESSONS 108): /cart/change.js silently no-ops a {} / null
   properties write — to clear the Dancer prop we blank the key to "".

   WRITES (optimistic + batched): every action applies its end state to the
   local model and renders IMMEDIATELY, then runs at most two requests
   behind it — bulk removals in one /cart/update.js ({updates:{key:0}}),
   re-adds in one /cart/add.js; a single-line property write stays one
   /cart/change.js. update/change responses ARE the full cart (used as the
   reconcile render); add.js returns only the added items, never the full
   cart, so those chains re-read /cart.js once. Input is pointer-blocked
   (no dim — the UI already shows the result) while a chain runs so a tap
   can't reference a stale or temporary line key.

   KEPT: localStorage roster (cs-dancers) as the KNOWN saved-name list;
   waiver attribute write via /cart/update.js; checkout gate; discount
   progress (per collection group — see DISCOUNT_GROUPS) in the footer; focus trap;
   focus-visible rings; header count badges; PDP add-to-cart intercept;
   cart:refresh listener; money via shop.money_format.
   CUT in V3 (Dylan): roster STRIP in the body (names live in-panel),
   undo notes, the "One step before checkout" waiver flag row.
   ============================================================ */
(function () {
  var drawer = document.getElementById('cart-drawer');
  var scrim = document.getElementById('cart-scrim');
  // The /cart page mounts a SECOND host (#cart-page) with the same .drawer scope
  // (class="drawer is-page"). Either surface alone is enough to boot; both share
  // the module's cart model + UI state so they stay in sync.
  var pageRoot = document.getElementById('cart-page');
  if ((!drawer || !scrim) && !pageRoot) return;

  var moneyFormat = (window.CartDrawer && window.CartDrawer.moneyFormat) || '${{amount}}';
  var lastFocus = null;

  // ---- HOSTS — every surface the cart renders into. The drawer keeps its
  //   open/close/focus-trap; the page host renders on load + after each write.
  //   { root, bodyEl, footEl, headEl, isDrawer }
  var hosts = [];
  if (drawer && scrim) {
    hosts.push({
      root: drawer, isDrawer: true,
      bodyEl: drawer.querySelector('[data-drawer-body]'),
      footEl: drawer.querySelector('[data-drawer-foot]'),
      headEl: drawer.querySelector('[data-drawer-count]')
    });
  }
  if (pageRoot) {
    hosts.push({
      root: pageRoot, isDrawer: false,
      bodyEl: pageRoot.querySelector('[data-drawer-body]'),
      footEl: pageRoot.querySelector('[data-drawer-foot]'),
      headEl: null
    });
  }
  // ACTIVE host pointers — render() points these at each host in turn, then runs
  // the (host-agnostic) render/wire functions that read these as globals. Keeps
  // the locked renderers untouched while letting them target any surface.
  var activeRoot = hosts[0] ? hosts[0].root : null;
  var body = hosts[0] ? hosts[0].bodyEl : null;
  var foot = hosts[0] ? hosts[0].footEl : null;
  var headCount = hosts[0] ? hosts[0].headEl : null;
  var activeIsDrawer = hosts[0] ? hosts[0].isDrawer : true;
  function useHost(h) {
    activeRoot = h.root; body = h.bodyEl; foot = h.footEl;
    headCount = h.headEl; activeIsDrawer = h.isDrawer;
  }

  // ---- keyboard-activate role="button" chips (P1-5a) ----------------
  // Every chip factory (pick/add/row) renders span[role=button][tabindex=0];
  // delegate one keydown listener per host root instead of wiring each chip —
  // survives the constant body.innerHTML re-renders since the root itself is
  // never replaced. The tri-state [data-pick] chips already call fire()
  // directly on their own keydown (below, wireCard) and preventDefault() first,
  // so by the time the event bubbles here e.defaultPrevented is already true —
  // this only fires for chips with no more specific handler.
  hosts.forEach(function (h) {
    h.root.addEventListener('keydown', function (e) {
      if (e.defaultPrevented) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var el = e.target.closest && e.target.closest('[role="button"]');
      if (!el) return;
      e.preventDefault();   // Space must not scroll the page
      el.click();
    });
  });

  // ---- discount config (C6 rebuild, 2026-07-13) ----
  // Per-collection groups rendered by snippets/cart-drawer.liquid from the two
  // automatic discounts' scopes (keiki 2+ → 10%, teens & adults 5+ → 20%; the
  // numbers live in theme settings → Class discounts). The bar only PROMISES a
  // discount the cart actually carries: applied state reads real per-line
  // /cart.js allocations, never re-derived math (LESSONS 113). The nudge state
  // ("add N more") is the only part driven by the configured thresholds.
  var DISCOUNT_GROUPS = (window.CartDrawer && window.CartDrawer.discountGroups) || null;

  // dancer property key written to line items (D69 — readable at fulfillment)
  var DANCER_KEY = 'Dancer';

  // ---- localStorage KNOWN saved-name list (key cs-dancers) ----
  // In V3 these are the names offered as chips in every who-panel. Graceful
  // when localStorage is unavailable (private mode).
  var ROSTER_KEY = 'cs-dancers';
  function loadKnown() {
    try {
      var raw = window.localStorage.getItem(ROSTER_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(function (n) { return typeof n === 'string' && n.trim(); }) : [];
    } catch (e) { return []; }
  }
  function saveKnown(list) {
    try { window.localStorage.setItem(ROSTER_KEY, JSON.stringify(list)); } catch (e) { /* disabled */ }
  }
  var KNOWN = loadKnown();
  function rememberName(name) {
    if (KNOWN.indexOf(name) === -1) { KNOWN.push(name); saveKnown(KNOWN); }
  }
  function forgetName(name) {
    KNOWN = KNOWN.filter(function (n) { return n !== name; });
    saveKnown(KNOWN);
  }

  // ---- per-card UI state that survives re-render, keyed by card class title.
  //   { expanded, revealed, editWho }
  var ui = {};
  // WAIVER STRIP (Option A + Dylan's mod, 2026-06-05; TRUE TOGGLE 2026-06-05b):
  // while ANY card is unassigned the waiver renders as a slim ~32px "Liability
  // waiver & media release" strip instead of the tall checkbox card, to save foot
  // space on the cart's first job. The strip is a TRUE TOGGLE: tapping it expands
  // to the FULL INTERACTIVE checkbox card (you can agree early); the expanded card
  // carries a collapse affordance (strip-style header row, chevron rotated) that
  // sets this flag back false to return to the strip. Collapsing an AGREED card
  // returns to the strip showing the agreed signal (the promo-dot tint) so the
  // agreement isn't buried. This module flag is shared so both hosts open/close
  // together. It's only consulted WHILE cards are unassigned — once every dancer
  // lands the card is always shown (auto-expanded, not collapsible: the final
  // pre-checkout state). (Supersedes the earlier "agreed cart never shows a strip"
  // edge-state — an agreed cart now CAN collapse to the agreed-signal strip.)
  var waiverExpanded = false;
  // transient per-render confirm/pick state (cleared on action)
  var ROW_CONFIRM = null;   // { cls, date, name }
  var ROW_PICK = null;      // { cls, date }
  var NAME_CONFIRM = null;  // { name, cls, uses }
  // destructive-action inline confirms (same envelope family as ROW_CONFIRM)
  var REMOVE_CONFIRM = null;     // { cls } — "Remove [class]?" (whole card)
  var DATE_CONFIRM = null;       // { cls, date } — date row × (removes a date for everyone)
  var CARDWIDE_CONFIRM = null;   // { cls, name } — unpress a dancer card-wide
  // clear every transient confirm/pick (a fresh destructive tap supersedes any open one)
  function clearTransient() {
    ROW_CONFIRM = ROW_PICK = NAME_CONFIRM = REMOVE_CONFIRM = DATE_CONFIRM = CARDWIDE_CONFIRM = null;
  }
  // remember the last cart so per-card wiring closures can re-read date/key maps
  var lastCart = null;

  // ---- money ----------------------------------------------------
  function formatMoney(cents) {
    var value = (cents / 100).toFixed(
      moneyFormat.indexOf('amount_no_decimals') > -1 ? 0 : 2
    );
    return moneyFormat.replace(/\{\{\s*(amount|amount_no_decimals)\s*\}\}/g, function () {
      return value;
    });
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function shortDate(when) { return String(when).split(' · ')[0]; }
  // "Friday, June 20th" → "June 20th" (drop weekday + any · time suffix). Used by
  // the date-removal confirm ("Remove June 20th?"). Falls back to shortDate when
  // there's no leading weekday clause.
  function monthDay(when) {
    var d = shortDate(when), c = d.indexOf(',');
    return c > -1 ? d.slice(c + 1).trim() : d;
  }
  // cart.js hands back the FULL-SIZE image URL — ask the CDN for a thumb
  // (56px box, up to 3× screens) so mobile isn't pulling megapixel originals
  function thumbUrl(src) {
    return src ? src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=200' : '';
  }

  // ---- header count badges --------------------------------------
  // [data-cart-open] lives in the header SECTION, so a customizer edit swaps
  // the button for a fresh node (site.js re-wire pattern / LESSONS 71):
  // resolve at call time, remember the last count, restamp after a reload.
  var lastCount = null;
  function setCount(n) {
    lastCount = n;
    document.querySelectorAll('[data-cart-open]').forEach(function (b) {
      b.setAttribute('data-count', String(n));
      var badge = b.querySelector('.nav-cart-count');
      if (badge) badge.textContent = n > 99 ? '99+' : String(n);
    });
    hosts.forEach(function (h) { if (h.headEl) h.headEl.textContent = String(n); });
  }
  document.addEventListener('shopify:section:load', function () {
    if (lastCount !== null) setCount(lastCount);
  });

  // ---- cart fetch -----------------------------------------------
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // ---- dancer property helpers ----------------------------------
  function dancerRaw(item) {
    var val = '';
    if (item.properties) {
      Object.keys(item.properties).forEach(function (k) {
        if (/^(who|dancer)/i.test(k) && item.properties[k]) val = item.properties[k];
      });
    }
    return val;
  }
  // parse to a list (legacy comma-join → array; "" → [])
  function dancersOf(item) {
    var val = dancerRaw(item);
    if (!val) return [];
    return String(val).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }
  // the line's other properties (everything except the dancer key, no hidden _ props)
  function otherProps(item) {
    var out = {};
    if (item.properties) {
      Object.keys(item.properties).forEach(function (k) {
        if (!/^(who|dancer)/i.test(k) && k.charAt(0) !== '_') out[k] = item.properties[k];
      });
    }
    return out;
  }
  // change.js properties payload setting the Dancer key to `name` (single) or
  // "" (unassigned), preserving the line's other props.
  // LESSONS 108: never send {} / delete-the-key — blank it to "".
  function dancerPropsSingle(item, name) {
    var props = otherProps(item);
    props[DANCER_KEY] = name ? name : '';
    return props;
  }
  function buildDancerProp(name) { var o = {}; o[DANCER_KEY] = name; return o; }

  // ============================================================
  //  RAW WRITES (low-level). All return a promise resolving to a cart.
  // ============================================================
  function postChange(payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }
  function postAdd(items, props) {
    var bodyObj = { items: items.map(function (it) {
      return { id: it.id, quantity: 1, properties: props || {} };
    }) };
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(bodyObj)
    }).then(function (r) { return r.json(); });
  }
  // bulk quantity writes — one request zeroes any number of lines by key
  function postUpdate(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }
  function zeroMap(keys) { var m = {}; keys.forEach(function (k) { m[k] = 0; }); return m; }

  // ============================================================
  //  BUSY GUARD — optimistic render first, writes behind it. `optimistic`
  //  mutates the local model (lastCart) and we render it immediately; the
  //  chain then runs pointer-blocked. change/update responses are full
  //  carts — render those directly; only add.js chains re-read /cart.js.
  // ============================================================
  var busy = false;
  function setBusy(on) {
    busy = on;
    hosts.forEach(function (h) {
      if (on) { h.root.setAttribute('aria-busy', 'true'); h.root.classList.add('is-busy'); }
      else { h.root.removeAttribute('aria-busy'); h.root.classList.remove('is-busy'); }
    });
  }
  function runChain(promiseFactory, optimistic) {
    if (busy) return Promise.resolve();
    setBusy(true);
    if (optimistic) { optimistic(); render(lastCart); }
    return Promise.resolve()
      .then(promiseFactory)
      .then(function (resp) {
        return (resp && resp.token && resp.items) ? resp : fetchCart();
      })
      .then(function (finalCart) {
        setBusy(false);
        render(finalCart);
        return finalCart;
      })
      .catch(function () {
        setBusy(false);
        return refresh();
      });
  }
  function chainWrites(cart, thunks) {
    var p = Promise.resolve(cart);
    thunks.forEach(function (thunk) { p = p.then(function (c) { return thunk(c); }); });
    return p;
  }

  // ============================================================
  //  NORMALIZE — enforce the invariant on every cart read.
  //  (a) legacy comma-joined "A, B" lines → one line per name.
  //  (b) qty N>1 lines → N qty-1 lines.
  //  Mechanism: change.js the line down to qty 1 (+ first name / blank),
  //  then add.js the same variant once per extra unit/name. Distinct
  //  Dancer props keep Shopify from re-merging units.
  // ============================================================
  function needsNormalize(cart) {
    return cart.items.some(function (it) {
      return it.quantity > 1 || dancersOf(it).length > 1;
    });
  }
  function variantIdOf(item) { return item.variant_id || item.id; }

  function normalizeCart(cart) {
    if (!needsNormalize(cart)) return Promise.resolve(cart);
    var thunks = [];
    cart.items.forEach(function (item) {
      var names = dancersOf(item);
      var qty = item.quantity;
      var count = Math.max(qty, names.length || 1);
      if (count <= 1 && names.length <= 1) return;
      var vid = variantIdOf(item);
      var key = item.key;
      var firstName = names.length ? names[0] : '';
      thunks.push(function () {
        return postChange({ id: key, quantity: 1, properties: dancerPropsSingle(item, firstName) });
      });
      for (var u = 1; u < count; u++) {
        var unitName = names.length ? (names[u] || '') : '';
        (function (nm) {
          thunks.push(function () {
            return postAdd([{ id: vid }], nm ? buildDancerProp(nm) : {});
          });
        })(unitName);
      }
    });
    if (!thunks.length) return Promise.resolve(cart);
    return chainWrites(cart, thunks);
  }

  // ============================================================
  //  REGISTRATIONS → V3 CARDS (one card per PRODUCT/class).
  // ============================================================
  function toRegs(cart) {
    var regs = [];
    cart.items.forEach(function (item) {
      var names = dancersOf(item);
      var dancer = names.length ? names[0] : '';   // normalized → ≤1 name
      var date = (item.variant_title && item.variant_title !== 'Default Title')
        ? item.variant_title : '';
      regs.push({
        cls: item.product_title,
        pid: item.product_id,
        date: date,
        dancer: dancer,
        key: item.key,
        vid: variantIdOf(item),
        unit: item.final_price,        // per-line price (one date, one spot)
        image: item.image,
        url: item.url
      });
    });
    return regs;
  }

  // chronological sort key for a variant title ("Wednesday, June 10th" →
  // month·day ordinal; unparseable titles sort last, keeping insertion order).
  // Adds (row "+", card-wide add) append lines, so cart order ≠ date order.
  var MONTH_IDX = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
  function dateKey(title) {
    var m = String(title).split(',').slice(1).join(',').match(/([A-Za-z]+)\s+(\d{1,2})/);
    if (!m) return 9999;
    var mi = MONTH_IDX[m[1].slice(0, 3).toLowerCase()];
    return mi == null ? 9999 : mi * 100 + parseInt(m[2], 10);
  }

  // Session-type line: a per-session enrollment carries a recurring-time
  // title ("Mondays @ 3:30–4:15pm"), not a single dated class. Such lines
  // sort first and use enrollment-worded removal copy (multi-session audit K-4).
  function isSession(title) { return /@/.test(String(title)); }

  // remembered card order across re-renders — the batched writes remove +
  // re-add lines, which reorders the raw cart; the UI must not jump.
  var cardOrder = [];

  // Build one card per product. A card aggregates ALL the product's lines.
  //   card = {
  //     cls, image, url, unit,
  //     dates: [date,…]            (chronological),
  //     perDate: { date: [name,…] } (assigned names on that date; "" lines add none),
  //     keys: { date: { name|'': [lineKey,…] } }  (line keys by date + dancer),
  //     vidByDate: { date: vid },
  //     unitByDate: { date: price }
  //   }
  function buildCards(regs) {
    var byCls = {};
    var order = [];
    regs.forEach(function (r) {
      if (!byCls[r.cls]) {
        byCls[r.cls] = {
          cls: r.cls, pid: r.pid, image: r.image, url: r.url, unit: r.unit,
          dates: [], perDate: {}, keys: {}, vidByDate: {}, unitByDate: {}
        };
        order.push(r.cls);
      }
      var c = byCls[r.cls];
      if (c.dates.indexOf(r.date) === -1) {
        c.dates.push(r.date);
        c.perDate[r.date] = [];
        c.keys[r.date] = {};
        c.vidByDate[r.date] = r.vid;
        c.unitByDate[r.date] = r.unit;
      }
      var who = r.dancer || '';
      if (who) c.perDate[r.date].push(who);
      c.keys[r.date][who] = (c.keys[r.date][who] || []);
      c.keys[r.date][who].push(r.key);
      // card-level unit is a FALLBACK only (optimistic paths before a date's
      // line exists) — a product can carry differently-priced variants at
      // once (Summer $15 dates + a Fall session enrollment), so all price
      // math must go through unitFor(card, date), never card.unit alone.
      if (!c.unit && r.unit) c.unit = r.unit;
    });
    order.forEach(function (cls) {
      byCls[cls].dates.sort(function (a, b) {
        var sa = isSession(a), sb = isSession(b);
        if (sa !== sb) return sa ? -1 : 1;   // session enrollments before dated classes (K-4)
        return dateKey(a) - dateKey(b);
      });
    });
    // cards already on screen keep their slot; new ones append
    var ordered = cardOrder.filter(function (cls) { return byCls[cls]; });
    order.forEach(function (cls) { if (ordered.indexOf(cls) === -1) ordered.push(cls); });
    cardOrder = ordered;
    return ordered.map(function (cls) { return byCls[cls]; });
  }

  // ---- derived helpers ----
  function dancersOnCard(card) {
    var seen = {};
    card.dates.forEach(function (d) { (card.perDate[d] || []).forEach(function (n) { seen[n] = true; }); });
    // KNOWN order first, then any cart names not in KNOWN (appended)
    var ordered = KNOWN.filter(function (n) { return seen[n]; });
    Object.keys(seen).forEach(function (n) { if (ordered.indexOf(n) === -1) ordered.push(n); });
    return ordered;
  }
  function isUnassigned(card) { return dancersOnCard(card).length === 0; }
  // CLASSES count: per date, max(1, dancers-on-that-date). A held (blank) spot
  // still counts as one class. Unit of sale everywhere a roll-up shows.
  function cardClasses(card) {
    var u = 0; card.dates.forEach(function (d) { u += Math.max(1, (card.perDate[d] || []).length); });
    return u;
  }
  function unitFor(card, date) {
    var u = (card.unitByDate || {})[date];
    return u == null ? card.unit : u;
  }
  function cardPrice(card) {
    var t = 0;
    card.dates.forEach(function (d) {
      t += Math.max(1, (card.perDate[d] || []).length) * unitFor(card, d);
    });
    return t;
  }
  function countByName(card) {
    var m = {}; card.dates.forEach(function (d) { (card.perDate[d] || []).forEach(function (n) { m[n] = (m[n] || 0) + 1; }); });
    return m;
  }
  // human summary in CLASSES language: "Leilani on all · Koa on 3"
  function whoSummary(card) {
    var m = countByName(card), N = card.dates.length;
    return dancersOnCard(card).map(function (n) {
      return escapeHtml(n) + (m[n] === N ? ' on all' : ' on ' + m[n]);
    }).join(' · ');
  }
  function totalClasses(cards) { var t = 0; cards.forEach(function (c) { t += cardClasses(c); }); return t; }
  // how many classes (date-spots) a name occupies across the whole cart
  function nameUses(cards, name) {
    var n = 0;
    cards.forEach(function (c) {
      c.dates.forEach(function (d) { if ((c.perDate[d] || []).indexOf(name) > -1) n++; });
    });
    return n;
  }
  function isSingle(card) { return card.dates.length === 1; }

  // ============================================================
  //  ATOM BUILDERS (port of the prototype's renderers).
  // ============================================================
  function readChip(name) {
    return '<span class="dchip dchip-read gs-chip" aria-pressed="true"><span class="dchip-dot"></span>' + escapeHtml(name) + '</span>';
  }
  function pickChips(card) {
    var m = countByName(card), N = card.dates.length;
    return KNOWN.map(function (n) {
      var c = m[n] || 0;
      var state = c === 0 ? 'false' : (c === N ? 'true' : 'mixed');
      var frac = state === 'mixed' ? ' <span class="dchip-frac t-tnum">' + c + '/' + N + '</span>' : '';
      return '<span class="dchip" role="button" tabindex="0" aria-pressed="' + state + '" data-pick data-name="' + escapeAttr(n) + '"><span class="dchip-dot"></span>' + escapeHtml(n) + frac + '</span>';
    }).join('');
  }
  function addChip() { return '<span class="dchip dchip-add" role="button" tabindex="0" data-addopen>+ Add dancer</span>'; }

  // the tinted who panel. o:{card, unassigned, nDates, noLabel, cards}
  function whoPanel(o) {
    var card = o.card, scope;
    if (o.nDates === 1) {
      scope = o.unassigned ? 'One class — just pick who&rsquo;s going.' : '';
    } else {
      scope = o.unassigned
        ? 'They&rsquo;ll be added to all ' + o.nDates + ' dates.'
        : 'Names added here join all ' + o.nDates + ' dates &middot; use <b>+</b> on a date for a single day.';
    }
    var st = ui[card.cls] || {};
    // deleting the LAST saved name while editing left the panel stuck in edit
    // mode with no chips, no Done and no "+ Add dancer" — with nothing left to
    // edit, edit mode is meaningless: fall back to the pick row.
    var editing = !!st.editWho && KNOWN.length > 0;
    var edit = KNOWN.length
      ? '<button class="who-edit t-overline" type="button" data-who-edit aria-pressed="' + (editing ? 'true' : 'false') + '">' + (editing ? 'Done' : 'Edit') + '</button>'
      : '';
    var heading = o.unassigned ? 'Assign a dancer to this class' : 'Who&rsquo;s dancing?';
    var label = o.noLabel
      ? (edit ? '<span class="who-label-row" style="justify-content:flex-end;">' + edit + '</span>' : '')
      : '<span class="who-label-row"><span class="t-overline who-label">' + (o.unassigned ? '<span class="warn-dot"></span>' : '') + heading + '</span>' + edit + '</span>';

    var chips;
    if (editing) {
      chips = KNOWN.map(function (n) {
        return '<span class="dchip" aria-pressed="false"><span class="dchip-dot"></span>' + escapeHtml(n) +
          '<button class="chip-x" type="button" data-del-name="' + escapeAttr(n) + '" aria-label="Remove ' + escapeAttr(n) + '">&times;</button></span>';
      }).join('');
    } else {
      chips = pickChips(card) + addChip();
    }

    var confirm = '';
    if (editing && NAME_CONFIRM && NAME_CONFIRM.cls === card.cls) {
      var nm = NAME_CONFIRM.name, uses = NAME_CONFIRM.uses;
      confirm = '<div class="row-confirm" style="margin-top:10px;">' +
        '<span class="rcf-msg t-caption">Remove ' + escapeHtml(nm) + '? ' + (uses === 1 ? 'This removes their class.' : 'This removes their ' + uses + ' classes.') + '</span>' +
        '<button class="rcf-go t-caption" type="button" data-name-go>Remove</button>' +
        '<button class="rcf-no t-caption" type="button" data-name-no>Keep</button>' +
      '</div>';
    }
    // card-wide unpress confirm (tap a fully-pressed pick chip) — takes the
    // dancer off EVERY date at once, so it gates behind a confirm like the
    // per-date row chip does.
    if (!editing && CARDWIDE_CONFIRM && CARDWIDE_CONFIRM.cls === card.cls) {
      var cwName = CARDWIDE_CONFIRM.name, cwN = countByName(card)[cwName] || 0;
      confirm = '<div class="row-confirm" style="margin-top:10px;">' +
        '<span class="rcf-msg t-caption">Take ' + escapeHtml(cwName) + ' off ' +
          (cwN === 1 ? 'this date?' : 'all ' + cwN + ' dates?') + '</span>' +
        '<button class="rcf-go t-caption" type="button" data-cardwide-go data-name="' + escapeAttr(cwName) + '">Remove</button>' +
        '<button class="rcf-no t-caption" type="button" data-cardwide-no>Keep</button>' +
      '</div>';
    }

    return '<div class="who-panel" data-card="' + escapeAttr(card.cls) + '">' +
        label +
        (scope && !editing ? '<span class="t-caption who-scope"' + (o.noLabel ? ' style="margin-top:0;"' : '') + '>' + scope + '</span>' : '') +
        '<div class="who-row"' + (scope || label ? '' : ' style="margin-top:9px;"') + ' data-who>' + chips + '</div>' +
        confirm +
      '</div>';
  }

  // one date row (state-aware: confirm + row-pick come from module state)
  function dateRow(card, when, unit) {
    var on = (card.perDate[when] || []);
    var rowUnits = Math.max(1, on.length);
    var rowPrice = rowUnits * unit;
    var count = rowUnits > 1 ? '<span class="d-count t-tnum">×' + rowUnits + '</span> ' : '';
    var chips = on.map(function (n) {
      return '<span class="dchip" role="button" tabindex="0" aria-pressed="true" data-rowchip data-date="' + escapeAttr(when) + '" data-name="' + escapeAttr(n) + '"><span class="dchip-dot"></span>' + escapeHtml(n) + '</span>';
    }).join('');
    var unassigned = isUnassigned(card);
    var rowAdd = (!unassigned)
      ? '<button class="row-add" type="button" data-rowadd data-date="' + escapeAttr(when) + '" aria-label="Add a dancer to this date only">+</button>'
      : '';
    var confirm = '';
    if (ROW_CONFIRM && ROW_CONFIRM.cls === card.cls && ROW_CONFIRM.date === when) {
      confirm = '<div class="row-confirm">' +
        '<span class="rcf-msg t-caption">Take ' + escapeHtml(ROW_CONFIRM.name) + ' off ' + escapeHtml(shortDate(when)) + ' only?</span>' +
        '<button class="rcf-go t-caption" type="button" data-rowgo>Remove</button>' +
        '<button class="rcf-no t-caption" type="button" data-rowno>Keep</button>' +
      '</div>';
    }
    var dateConfirm = '';
    if (DATE_CONFIRM && DATE_CONFIRM.cls === card.cls && DATE_CONFIRM.date === when) {
      dateConfirm = '<div class="row-confirm">' +
        '<span class="rcf-msg t-caption">Remove ' + (isSession(when) ? 'this enrollment' : escapeHtml(monthDay(when))) + '?</span>' +
        '<button class="rcf-go t-caption" type="button" data-datego data-date="' + escapeAttr(when) + '">Remove</button>' +
        '<button class="rcf-no t-caption" type="button" data-dateno>Keep</button>' +
      '</div>';
    }
    var rowPick = '';
    if (ROW_PICK && ROW_PICK.cls === card.cls && ROW_PICK.date === when) {
      var offRow = KNOWN.filter(function (n) { return on.indexOf(n) === -1; });
      rowPick = '<div class="row-pick">' +
        '<span class="rp-cap t-caption">Add to ' + escapeHtml(shortDate(when)) + ' only</span>' +
        offRow.map(function (n) {
          return '<span class="dchip" role="button" tabindex="0" aria-pressed="false" data-rowpickname data-date="' + escapeAttr(when) + '" data-name="' + escapeAttr(n) + '"><span class="dchip-dot"></span>' + escapeHtml(n) + '</span>';
        }).join('') +
        '<span class="dchip dchip-add" role="button" tabindex="0" data-rowpicknew data-date="' + escapeAttr(when) + '">+ New name</span>' +
        '<button class="rcf-no t-caption" type="button" data-rowpickcancel style="margin-left:auto;">Cancel</button>' +
      '</div>';
    }
    return '<li class="d-row">' +
      '<span class="d-when t-caption">' + escapeHtml(shortDate(when)) + '</span>' +
      '<span class="d-chips">' + chips + rowAdd + '</span>' +
      '<span class="t-caption t-tnum d-price">' + count + formatMoney(rowPrice) + '</span>' +
      '<button class="line-x" type="button" data-removedate data-date="' + escapeAttr(when) + '" aria-label="' + (isSession(when) ? 'Remove this enrollment' : 'Remove this date') + '">&times;</button>' +
      confirm + dateConfirm + rowPick +
    '</li>';
  }
  function datesList(card) {
    var rows = card.dates.map(function (w) { return dateRow(card, w, unitFor(card, w)); }).join('');
    return '<ul class="dates-list t-caption" data-list>' + rows + '</ul>';
  }

  // `subHtml` and `countHtml` arrive pre-escaped. 2×2 baseline grid (Dylan
  // 2026-06-05): row 1 = title | price, row 2 = day/time | count — the meta
  // and the count share a row and align on their text baselines, and the
  // count column lines up with the per-date row prices + subtotal.
  function groupHead(card, price, subHtml, countHtml) {
    return '<div class="group-head">' +
      '<a class="cart-thumb" href="' + escapeAttr(card.url || '#') + '">' + (card.image ? '<img src="' + escapeHtml(thumbUrl(card.image)) + '" alt="' + escapeAttr(card.cls) + '" width="56" height="56" loading="lazy" decoding="async">' : '') + '</a>' +
      '<div class="group-head-grid">' +
        '<a class="t-h4 group-title" href="' + escapeAttr(card.url || '#') + '">' + escapeHtml(card.cls) + '</a>' +
        '<div class="t-h4 t-tnum group-price">' + formatMoney(price) + '</div>' +
        (subHtml ? '<div class="t-caption group-meta">' + subHtml + '</div>' : '') +
        (countHtml || '') +
      '</div>' +
    '</div>';
  }
  function removeAction(card) {
    var confirm = '';
    if (card && REMOVE_CONFIRM && REMOVE_CONFIRM.cls === card.cls) {
      var n = cardClasses(card);
      confirm = '<div class="row-confirm" style="margin-top:10px;">' +
        '<span class="rcf-msg t-caption">Remove ' + escapeHtml(card.cls) + '? This removes ' +
          (n === 1 ? '1 class.' : 'all ' + n + ' classes.') + '</span>' +
        '<button class="rcf-go t-caption" type="button" data-remove-go>Remove</button>' +
        '<button class="rcf-no t-caption" type="button" data-remove-no>Keep</button>' +
      '</div>';
    }
    return '<div class="group-actions"><button class="group-remove t-caption" type="button" data-remove>Remove class</button></div>' + confirm;
  }

  // ============================================================
  //  OPTION C CARD — one thing at a time.
  // ============================================================
  // schedule label: single-date = its one date; multi-date = the shared
  // weekday pluralized ("Thursdays"), or nothing when weekdays differ —
  // a multi-date title must NEVER show one specific date (locked v3 rule).
  // Both get the class TIME appended ("Wednesdays · 3–4 pm") from the
  // class.schedule_pretty map Liquid exposes (variant titles are date-only;
  // /cart.js can't see metafields). Map filled by cart-drawer.liquid for
  // lines in the cart at page load + product.liquid for the PDP product.
  function scheduleFull(card) {
    var map = (window.CartDrawer && window.CartDrawer.schedules) || {};
    return map[String(card.pid)] || '';
  }
  function scheduleTime(card) {
    var sp = scheduleFull(card);
    if (!sp) return '';
    // Multi-slot classes join every weekly slot ("Mon 5:30–6 pm · Sat 10–10:30 am").
    // When ALL of the card's dates share one weekday, show only that slot's time
    // (Dylan 2026-07-15 — a Monday card was printing Saturday's time too).
    var parts = sp.split(' · ');
    if (parts.length > 1) {
      var wd = String(card.dates[0] || '').split(',')[0].trim().slice(0, 3).toLowerCase();
      var sameDay = !!wd && card.dates.every(function (d) {
        return String(d || '').split(',')[0].trim().slice(0, 3).toLowerCase() === wd;
      });
      if (sameDay) {
        for (var i = 0; i < parts.length; i++) {
          if (parts[i].trim().slice(0, 3).toLowerCase() === wd) { sp = parts[i].trim(); break; }
        }
      }
    }
    var at = sp.search(/\d/);           // "Wed 3–4 pm" → "3–4 pm"
    return at > -1 ? sp.slice(at) : '';
  }
  function scheduleLabel(card) {
    var time = scheduleTime(card);
    if (card.dates.length === 1) {
      var d = card.dates[0] || '';
      return d && time ? d + ' · ' + time : (d || '');
    }
    var first = String(card.dates[0] || '').split(',')[0].trim();
    if (!first) return '';
    for (var i = 1; i < card.dates.length; i++) {
      if (String(card.dates[i] || '').split(',')[0].trim() !== first) {
        // weekdays differ — schedule_pretty verbatim ("Mon & Wed 4–5 pm") or nothing
        return scheduleFull(card);
      }
    }
    return first + 's' + (time ? ' · ' + time : '');
  }

  function cCard(card) {
    var nDates = card.dates.length;
    var price = cardPrice(card);
    var st = ui[card.cls] || {};
    var unassigned = isUnassigned(card);
    var classes = cardClasses(card);
    var classesLbl = classes + ' ' + (classes === 1 ? 'class' : 'classes'); /* copy rule: parent-facing noun is "class" (REGISTRATION_FLOW_REDESIGN.md) */
    var schedule = scheduleLabel(card);
    var subLbl = schedule ? escapeHtml(schedule) : '';
    var single = isSingle(card);

    // UNASSIGNED → head where the count under the price IS the expander
    // (multi-date), then the peeked dates (when open), then the who panel
    // LAST.
    if (unassigned) {
      var panel = whoPanel({ card: card, unassigned: true, nDates: nDates });
      var peek = !!st.revealed;
      var countHtml = single
        ? '<div class="t-caption group-count">' + classesLbl + '</div>'
        : '<button class="sub-toggle group-count t-caption" type="button" aria-expanded="' + (peek ? 'true' : 'false') + '" data-toggle-finetune>' +
            classesLbl + ' <span class="pl-caret" aria-hidden="true">›</span>' +
          '</button>';
      var head = groupHead(card, price, subLbl, countHtml);
      var peekList = (!single && peek) ? '<div class="dates-wrap" style="margin-top:8px;">' + datesList(card) + '</div>' : '';
      return '<div class="group is-unassigned" data-card="' + escapeAttr(card.cls) + '" data-unassigned="true">' +
        head + peekList + panel + removeAction(card) + '</div>';
    }

    // ASSIGNED + collapsed → settled summary row (no undo note in V3)
    var expanded = !!st.expanded;
    var readChipsHTML = dancersOnCard(card).map(readChip).join('');
    if (!expanded) {
      return '<div class="group is-collapsed" data-card="' + escapeAttr(card.cls) + '" data-unassigned="false">' +
        '<div class="group-summary">' +
          '<div class="gs-main">' +
            '<a class="t-h4 gs-title" href="' + escapeAttr(card.url || '#') + '">' + escapeHtml(card.cls) + '</a>' + /* titles link back to the class page (Dylan 2026-07-15), matching the unassigned card */
            (subLbl ? '<div class="t-caption gs-sub">' + subLbl + '</div>' : '') +
          '</div>' +
          '<span class="gs-chips">' + readChipsHTML + '</span>' +
          '<span class="gs-price-col"><span class="t-h4 t-tnum gs-price">' + formatMoney(price) + '</span><span class="t-caption gs-count">' + classesLbl + '</span></span>' +
          '<button class="gs-edit" type="button" data-expand aria-label="Expand class"><span class="gs-caret" aria-hidden="true">⌄</span></button>' +
        '</div>' +
      '</div>';
    }

    // ASSIGNED + expanded → head + panel + "Fine-tune classes" toggle
    var revealed = !!st.revealed;
    var panel2 = whoPanel({ card: card, unassigned: false, nDates: nDates });
    var ft = single ? '' :
      '<button class="finetune t-caption" type="button" aria-expanded="' + (revealed ? 'true' : 'false') + '" data-toggle-finetune>' +
        '<span>' + (revealed ? 'Hide dates' : 'Fine-tune dates') + '</span><span class="ft-caret" aria-hidden="true">›</span>' +
      '</button>' +
      (revealed ? '<div class="dates-wrap" style="margin-top:8px;">' + datesList(card) + '</div>' : '');
    return '<div class="group" data-card="' + escapeAttr(card.cls) + '" data-unassigned="false">' +
      '<div class="group-summary gs-exhead">' +
        '<div class="gs-main">' +
          '<a class="t-h4 gs-title" href="' + escapeAttr(card.url || '#') + '">' + escapeHtml(card.cls) + '</a>' + /* titles link back to the class page (Dylan 2026-07-15), matching the unassigned card */
          (subLbl ? '<div class="t-caption gs-sub">' + subLbl + '</div>' : '') +
        '</div>' +
        '<span class="gs-price-col"><span class="t-h4 t-tnum gs-price">' + formatMoney(price) + '</span><span class="t-caption gs-count">' + classesLbl + '</span></span>' +
        '<button class="gs-edit" type="button" data-expand aria-label="Collapse class"><span class="gs-caret" aria-hidden="true" style="transform:rotate(180deg);">⌄</span></button>' +
      '</div>' +
      panel2 + ft + removeAction(card) + '</div>';
  }

  // ============================================================
  //  FOOTER — discount progress (CLASSES = item_count) + subtotal +
  //  compact waiver + checkout gate.
  // ============================================================
  function renderEmpty() {
    var html = '<div class="empty-wrap">';
    html += '<span class="t-h4" style="color:var(--ink);">Your cart is empty.</span>';
    html += '<p class="t-body-sm" style="color:var(--ink-2);">Browse classes for keiki or teens + adults, pick a date, and we&rsquo;ll save your spot.</p>';
    html += '<div style="display:flex; gap:10px; flex-wrap:wrap;">';
    html += '<a href="/pages/classes-keiki" class="btn btn-secondary">Keiki classes <span class="btn-arrow">→</span></a>';
    html += '<a href="/pages/classes-teens-adults" class="btn btn-secondary">Teens + adults <span class="btn-arrow">→</span></a>';
    html += '</div></div>';
    return html;
  }

  // Per-group cart stats: count = qty of lines whose product is in the group's
  // collection; saved = the REAL discount those lines carry per /cart.js
  // line_level_discount_allocations (what checkout will honor — LESSONS 113).
  function discountStats(cart) {
    if (!DISCOUNT_GROUPS) return [];
    var stats = [];
    Object.keys(DISCOUNT_GROUPS).forEach(function (key) {
      var g = DISCOUNT_GROUPS[key];
      if (!g || !g.min || !g.ids || !g.ids.length) return;
      var count = 0, saved = 0;
      (cart.items || []).forEach(function (it) {
        if (g.ids.indexOf(it.product_id) === -1) return;
        /* keiki groups are VARIANT-scoped in admin (2026-07-15): mode 'slots' =
           session-slot variants (titled "Mondays @ 5:30–6", the "@" convention),
           mode 'dates' = plain date variants. No mode = every line counts (ta). */
        if (g.mode) {
          var isSlot = /@/.test(it.variant_title || '');
          if (g.mode === 'slots' && !isSlot) return;
          if (g.mode === 'dates' && isSlot) return;
        }
        count += it.quantity;
        (it.line_level_discount_allocations || []).forEach(function (a) { saved += a.amount || 0; });
      });
      if (count > 0) stats.push({ key: key, label: g.label, pct: g.pct, min: g.min, count: count, saved: saved });
    });
    return stats;
  }

  // One progress row per discount group present in the cart.
  //   applied   — its lines carry real allocations: state the saving as fact.
  //   unlocked but not applied — Dylan enabled discount COMBINING 2026-07-15
  //     (admin: Combinations → other product discounts), so a qualified group
  //     normally always carries its allocation; if Shopify still shows none
  //     (discount paused/deleted in admin), render no promise at all.
  //   locked    — nudge toward the threshold.
  function renderPromo(cart) {
    var stats = discountStats(cart);
    if (!stats.length) return '';
    var html = '';
    stats.forEach(function (s) {
      var applied = s.saved > 0;
      var unlocked = s.count >= s.min;
      var caption;
      if (applied) {
        caption = s.pct + '% off ' + s.label + ' — saving ' + formatMoney(s.saved);
      } else if (unlocked) {
        return; // qualifies on paper, cart shows no discount — don't promise
      } else {
        var remaining = s.min - s.count;
        /* the nudge names WHAT to add (Dylan 2026-07-15): "Add 1 more teens & adults
           class for 20% off" — label singularized when remaining === 1 */
        var thing = remaining === 1 ? s.label.replace(/classes\b/, 'class') : s.label;
        caption = 'Add ' + remaining + ' more ' + thing + ' for ' + s.pct + '% off';
      }
      var fill = Math.min(100, Math.round((s.count / s.min) * 100));
      html += '<div class="promo' + (applied ? ' is-unlocked' : '') + '" data-promo="' + s.key + '">';
      html += '<div class="promo-caption t-caption">';
      if (applied) html += '<span class="promo-dot"></span>';
      html += '<span data-promo-caption>' + escapeHtml(caption) + '</span></div>';
      html += '<div class="promo-track"><div class="promo-fill" data-promo-fill style="width:' + fill + '%;"></div></div>';
      html += '</div>';
    });
    return html;
  }

  // The drawer shows a COMPACT waiver (4 paras). The /cart page keeps the
  // FULLER legal text (adds Medical Authorization + Conduct) — that surface has
  // the room and it's the page-of-record. Same checkbox / attribute / gate.
  function waiverFullHtml() {
    var common =
      '<p><strong>Acknowledgment of Risk and Waiver of Liability.</strong> I understand that participation in dance classes, rehearsals, and performances with Center Stage Dance Alliance (CSDA) involves physical activity and carries inherent risks, including but not limited to: slips, falls, collisions, abrasions, muscle strains, sprains, broken bones, or other injuries.</p>' +
      '<p>By typing my name at registration, I voluntarily assume all risks associated with my own participation and/or my child&rsquo;s participation in any CSDA class, rehearsal, event, or production.</p>' +
      '<p>I hereby release, waive, and hold harmless CSDA, its instructors, staff, officers, and volunteers from any and all claims, demands, or causes of action arising from ordinary negligence resulting in personal injury, illness, or property damage, whether occurring during classes, rehearsals, events, or productions.</p>';
    if (activeIsDrawer) {
      return common +
        '<p><strong>Media Release (Photo &amp; Video Consent).</strong> I grant CSDA permission to photograph, record, and use images or videos of myself and/or my child in connection with CSDA events, classes, or productions.</p>';
    }
    return common +
      '<p>I understand that this release does not apply to claims resulting from gross negligence, reckless conduct, or intentional acts by CSDA or its representatives.</p>' +
      '<p><strong>Medical Authorization.</strong> In the event of injury or medical emergency, I authorize CSDA staff to obtain necessary medical treatment for myself or my child, and I accept full financial responsibility for such treatment.</p>' +
      '<p><strong>Media Release (Photo &amp; Video Consent).</strong> I grant CSDA permission to photograph, record, and use images or videos of myself and/or my child in connection with CSDA events, classes, or productions. Such media may be used, without compensation, for educational, promotional, or advertising purposes in print, online, or social media.</p>' +
      '<p><strong>Conduct.</strong> We maintain a zero-tolerance policy for bullying of any kind. Every dancer deserves to feel safe, respected, and supported in our studio, and any behavior that harms, excludes, or intimidates others is not accepted and may result in removal from class or the program.</p>';
  }

  function renderFoot(cart) {
    if (cart.item_count === 0) { foot.innerHTML = ''; foot.style.display = 'none'; return; }
    foot.style.display = '';
    var agreed = (cart.attributes && cart.attributes['Waiver Agreement']) ? true : false;
    var html = '';
    // STACK ORDER (Dylan 2026-06-05): subtotal row FIRST, then the discount promo
    // UNDER it — the money fact leads, the "add N more for 20% off" nudge follows.
    // (was promo-above-subtotal; flipped here, both hosts inherit.) Then waiver,
    // then checkout. .money-block's flex gap holds the 4pt ladder spacing.
    html += '<div class="money-block">';
    html += '<div class="subtotal-row">';
    html += '<span class="t-h3-display" style="color:var(--ink-2);">Subtotal</span>'; /* display face at h3 size (token minted for this pair) — Dylan 2026-07-15 */
    html += '<span class="t-h3-display" data-subtotal style="color:var(--ink);">' + formatMoney(cart.total_price) + '</span>';
    html += '</div>';
    html += renderPromo(cart);
    html += '</div>';

    // WAIVER — strip-as-true-toggle (Option A + Dylan's mod). While ANY card is
    // unassigned the waiver is a TWO-STATE TOGGLE: the slim strip (collapsed) or
    // the full interactive checkbox card (expanded), driven by waiverExpanded. The
    // foot's first job is dancer assignment, so it lands collapsed; tapping either
    // the strip (expand) or the card's collapse header (collapse) flips the flag
    // and re-renders both hosts in step. Agreement no longer forces the card open —
    // an agreed card can collapse back to the AGREED-SIGNAL strip (promo-dot tint),
    // which keeps the agreement legible without claiming card space. Once every
    // card is assigned (needsDancer === 0) the toggle retires: the card is always
    // shown and NOT collapsible — that's the final pre-checkout state.
    // needsDancer counts unassigned cards from the shared model (same signal
    // refreshGate reads off the painted body); on an empty foot we already returned.
    var needsDancer = 0;
    cards.forEach(function (c) { if (isUnassigned(c)) needsDancer++; });
    var collapsible = needsDancer > 0;          // toggle is live only while unassigned
    var showStrip = collapsible && !waiverExpanded;

    if (showStrip) {
      // Option A strip: "Liability waiver & media release" overline + chevron on
      // the quiet alt surface. ~32px tall (7px ladder padding on a t-overline
      // line). aria-expanded=false; tapping it sets waiverExpanded → full card.
      // AGREED variant: .is-agreed adds the promo-dot ahead of the label (same
      // agreed-signal language as the card's tint) so a collapsed agreement reads
      // as done, not outstanding.
      html += '<button type="button" class="wv-strip' + (agreed ? ' is-agreed' : '') + '" aria-expanded="false" data-waiver-strip>';
      if (agreed) html += '<span class="promo-dot" aria-hidden="true"></span>';
      html += '<span class="wv-strip-label t-overline">Liability waiver &amp; media release</span>';
      if (agreed) html += '<span class="wv-strip-state t-caption">Agreed</span>';
      html += '<span class="wv-strip-chev" aria-hidden="true">&#9662;</span>';
      html += '</button>';
    } else {
      // FULL INTERACTIVE CARD — no "One step before checkout" flag row. Caption-
      // size text; the required-mark * + unchecked tint + gate hint carry the
      // signal. "Read full waiver" link is INLINE but sits OUTSIDE the <label> so
      // clicking it can't toggle the checkbox (the agree text toggles it).
      // page host shows the full text open by default (it's the page-of-record);
      // the drawer keeps it collapsed behind "Read full waiver".
      var waiverOpen = !activeIsDrawer;
      html += '<div class="waiver-card-a' + (agreed ? ' is-agreed' : '') + (collapsible ? ' is-collapsible' : '') + '" data-waiver-card>';
      // TEXT-SWAP TOGGLE (Dylan 2026-06-05c): the expanded card carries NO heading
      // row — the checkbox's "I agree to the Liability Waiver & Media Release."
      // sentence IS the label, so a separate heading repeats it. While the toggle is
      // live (a card still unassigned) the only collapse affordance is the chevron,
      // rendered in the SAME top-right slot the strip's chevron occupies, rotated up
      // (aria-expanded=true) → tapping it sets waiverExpanded=false (wireFoot) and
      // swaps back to the strip. Once every dancer lands the card is the final state
      // (not collapsible) and the chevron isn't rendered — so the collapsible-expanded
      // and all-assigned presentations are identical aside from this chevron.
      if (collapsible) {
        html += '<button type="button" class="wv-collapse" aria-expanded="true" data-waiver-collapse aria-label="Collapse liability waiver">';
        html += '<span class="wv-strip-chev" aria-hidden="true">&#9662;</span>';
        html += '</button>';
      }
      html += '<div class="waiver-check" style="display:flex; align-items:flex-start; gap:10px;">';
      // P1-5e: id + aria-labelledby (not a real <label for>) — the label
      // deliberately has no `for` so clicking it doesn't ALSO natively toggle
      // the checkbox on top of the manual toggle wireFoot() already does
      // (see wireFoot's data-waiver-label handler note); aria-labelledby gives
      // the input its accessible name without touching that click semantics.
      // IDs are suffixed per host (drawer/page) — both #cart-drawer and
      // #cart-page can be mounted at once on /cart, and a bare id would collide.
      var waiverId = 'cd-waiver-' + (activeIsDrawer ? 'drawer' : 'page');
      var waiverLabelId = waiverId + '-label';
      // round ink-checkbox (registration redesign, 2026-07-14): visually-hidden
      // native input + decorative .ink-checkbox-dot sibling (base.css). The
      // real checkbox keeps id/aria-labelledby/data-waiver/checked exactly as
      // before — only the visual indicator changed, so wireFoot()'s change
      // listener and every other reference to [data-waiver] is untouched.
      html += '<span class="ink-checkbox">';
      html += '<input type="checkbox" id="' + waiverId + '" data-waiver aria-labelledby="' + waiverLabelId + '"' + (agreed ? ' checked' : '') + '>';
      html += '<span class="ink-checkbox-dot" aria-hidden="true"><svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1.5" stroke="var(--cream)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      html += '</span>';
      html += '<span class="t-caption" style="color:var(--ink);">';
      html += '<label data-waiver-label id="' + waiverLabelId + '" style="cursor:pointer;"><b>I agree to the Liability Waiver &amp; Media Release.</b></label>';
      html += '<span class="wv-req" aria-hidden="true">*</span> ';
      html += '<span class="lnk" data-readwaiver style="cursor:pointer;">' + (waiverOpen ? 'Hide waiver' : 'Read full waiver') + '</span>';
      html += '</span>';
      html += '</div>';
      html += '<div class="waiver-full t-caption" data-waivertext data-open="' + (waiverOpen ? 'true' : 'false') + '">';
      html += waiverFullHtml();
      html += '</div></div>';
    }
    html += '<button class="btn btn-primary w-full justify-center" data-checkout style="margin-top:14px; width:100%;" disabled aria-disabled="true">';
    html += 'Check out <span class="btn-arrow">→</span></button>';
    html += '<div class="foot-hint t-caption" data-hint><span class="warn-dot"></span><span>Agree to the waiver to check out.</span></div>';
    foot.innerHTML = html;
    wireFoot();
  }

  // ============================================================
  //  FULL RENDER from a live cart.
  // ============================================================
  var cards = [];
  function render(cart) {
    lastCart = cart;
    setCount(cart.item_count);
    // build the model once (shared across hosts), then paint each surface.
    var empty = cart.item_count === 0;
    if (empty) { cards = []; cardOrder = []; }
    else { cards = buildCards(toRegs(cart)); }

    hosts.forEach(function (h) {
      useHost(h);
      if (!body) return;
      if (empty) {
        // drawer: render its JS empty-state. page: it lands non-empty (the host
        // only mounts when the cart has items), but a live transition to empty
        // (removing the last class) reloads to the server-rendered empty page so
        // the header h1 ("Your cart is empty.") and empty markup are authoritative.
        if (activeIsDrawer) { body.innerHTML = renderEmpty(); }
        else { window.location.assign(window.location.pathname); return; }
      } else {
        var html = '<div data-cards>';
        html += cards.map(cCard).join('');
        html += '</div>';
        body.innerHTML = html;
        cards.forEach(function (card) { wireCard(card); });
      }
      renderFoot(cart);
      refreshGate();
    });
  }

  // ---- find a card by class title from the current render ----
  function cardForCls(cls) {
    for (var i = 0; i < cards.length; i++) if (cards[i].cls === cls) return cards[i];
    return null;
  }
  function cardFromEl(el) {
    var host = el.closest('[data-card]');
    return host ? cardForCls(host.getAttribute('data-card')) : null;
  }

  // refresh = fetch (+ normalize only when a line actually needs it), render.
  function refresh() {
    return fetchCart().then(function (cart) {
      if (!needsNormalize(cart)) { render(cart); return cart; }
      return normalizeCart(cart)
        .then(function () { return fetchCart(); })
        .then(function (c) { render(c); return c; });
    });
  }

  // ---- waiver attribute ----
  function setWaiverAttribute(agreed) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { 'Waiver Agreement': agreed ? 'Agreed ' + new Date().toISOString() : '' } })
    });
  }

  // ---- gate: enable checkout only when zero unassigned cards + waiver ----
  // bEl/fEl default to the active host's body/foot; callers in host-specific
  // event handlers pass their captured elements so the right surface updates.
  function refreshGate(bEl, fEl) {
    bEl = bEl || body; fEl = fEl || foot;
    if (!bEl || !fEl) return;
    var checkout = fEl.querySelector('[data-checkout]');
    if (!checkout) return;
    var hint = fEl.querySelector('[data-hint]');
    var needsDancer = bEl.querySelectorAll('.group.is-unassigned').length;
    var waiver = fEl.querySelector('[data-waiver]');
    var waiverOk = waiver && waiver.checked;
    var ok = needsDancer === 0 && waiverOk;
    if (ok) {
      checkout.removeAttribute('disabled'); checkout.removeAttribute('aria-disabled');
      if (hint) hint.style.display = 'none';
    } else {
      checkout.setAttribute('disabled', 'disabled'); checkout.setAttribute('aria-disabled', 'true');
      if (hint) {
        hint.style.display = '';
        var msg = hint.querySelector('span:last-child');
        if (msg) {
          if (needsDancer > 0 && !waiverOk) {
            msg.textContent = needsDancer + (needsDancer === 1 ? ' class needs' : ' classes need') + ' a dancer · agree to the waiver to check out.';
          } else if (needsDancer > 0) {
            msg.textContent = needsDancer + (needsDancer === 1 ? ' class still needs a dancer.' : ' classes still need a dancer.');
          } else {
            msg.textContent = 'Agree to the waiver to check out.';
          }
        }
      }
    }
  }

  // ============================================================
  //  LOCAL MODEL EDITS — mutate lastCart so the optimistic render shows
  //  the action's end state before any request returns.
  // ============================================================
  function itemByKey(key) {
    if (!lastCart) return null;
    for (var i = 0; i < lastCart.items.length; i++) if (lastCart.items[i].key === key) return lastCart.items[i];
    return null;
  }
  function locRecount() {
    var n = 0, t = 0;
    lastCart.items.forEach(function (it) {
      n += it.quantity;
      t += (it.final_line_price != null ? it.final_line_price : it.final_price * it.quantity);
    });
    // no optimistic discount math — final_line_price already carries any
    // line-level automatic discount, and guessing the threshold here either
    // double-discounts or invents discounts the store won't apply (the live
    // 5+/20% discount's product scope is wrong as of 2026-06-05 — see
    // changelog). The reconcile render settles the exact figure.
    lastCart.item_count = n;
    lastCart.total_price = t;
  }
  function locSetDancer(key, name) {
    var it = itemByKey(key);
    if (it) it.properties = dancerPropsSingle(it, name);
  }
  function locRemove(keys) {
    if (!keys.length) return;
    lastCart.items = lastCart.items.filter(function (it) { return keys.indexOf(it.key) === -1; });
    locRecount();
  }
  var tmpSeq = 0;   // temp keys for optimistic adds; reconcile swaps in the real ones
  function locAdd(card, date, name) {
    lastCart.items.push({
      key: 'tmp:' + (++tmpSeq),
      variant_id: card.vidByDate[date], variant_title: date,
      product_id: card.pid, product_title: card.cls,
      url: card.url, image: card.image,
      quantity: 1, final_price: unitFor(card, date), final_line_price: unitFor(card, date),
      properties: name ? buildDancerProp(name) : {}
    });
    locRecount();
  }

  // ============================================================
  //  ACTION → LIVE WRITE mappers (V3 semantics, batched).
  // ============================================================

  // shared executor for removal-shaped actions. specs: [{key, blank, vid}] —
  // blank=true means the paid spot stays (the line is re-added unnamed).
  // One spec + blank → a single change.js property write (response = cart);
  // otherwise ONE update.js zeroes every key, then ONE add.js restores blanks.
  function runSpecs(specs) {
    if (!specs.length) return;
    var blanks = specs.filter(function (s) { return s.blank; });
    var writes;
    if (specs.length === 1 && blanks.length === 1) {
      var item = itemByKey(specs[0].key);
      if (!item) return;
      var payload = { id: specs[0].key, properties: dancerPropsSingle(item, '') };
      writes = function () { return postChange(payload); };
    } else {
      var zeros = zeroMap(specs.map(function (s) { return s.key; }));
      var adds = blanks.map(function (s) { return { id: s.vid }; });
      writes = function () {
        var p = postUpdate(zeros);
        return adds.length ? p.then(function () { return postAdd(adds, {}); }) : p;
      };
    }
    runChain(writes, function () {
      specs.forEach(function (s) { if (s.blank) locSetDancer(s.key, ''); });
      locRemove(specs.filter(function (s) { return !s.blank; }).map(function (s) { return s.key; }));
    });
  }
  // per-date specs for `name` on a card (sole-holder → blank, else remove)
  function specsForName(card, name, dates) {
    var specs = [];
    dates.forEach(function (d) {
      var on = card.perDate[d] || [];
      if (on.indexOf(name) === -1) return;
      var sole = on.length === 1;
      ((card.keys[d] && card.keys[d][name]) || []).forEach(function (k) {
        specs.push({ key: k, blank: sole, vid: card.vidByDate[d] });
      });
    });
    return specs;
  }
  function keysOfDate(card, when) {
    var keys = [], byWho = card.keys[when] || {};
    Object.keys(byWho).forEach(function (who) { byWho[who].forEach(function (k) { keys.push(k); }); });
    return keys;
  }

  // assign `name` to EVERY blank line of the card (first-dancer assign).
  // One blank → one change.js; N blanks → one update.js + one add.js.
  function assignAllBlank(card, name) {
    rememberName(name);
    var blanks = [];   // [{key, date}]
    card.dates.forEach(function (d) {
      ((card.keys[d] && card.keys[d]['']) || []).forEach(function (k) { blanks.push({ key: k, date: d }); });
    });
    if (!blanks.length) return;
    var writes;
    if (blanks.length === 1) {
      var item = itemByKey(blanks[0].key);
      if (!item) return;
      var payload = { id: blanks[0].key, properties: dancerPropsSingle(item, name) };
      writes = function () { return postChange(payload); };
    } else {
      var zeros = zeroMap(blanks.map(function (b) { return b.key; }));
      var adds = blanks.map(function (b) { return { id: card.vidByDate[b.date] }; });
      writes = function () {
        return postUpdate(zeros).then(function () { return postAdd(adds, buildDancerProp(name)); });
      };
    }
    runChain(writes, function () {
      blanks.forEach(function (b) { locSetDancer(b.key, name); });
    });
  }

  // add `name` card-wide: ONE add.js with every date they're not on.
  function addDancerCardWide(card, name) {
    rememberName(name);
    var dates = card.dates.filter(function (d) { return (card.perDate[d] || []).indexOf(name) === -1; });
    if (!dates.length) return;
    var items = dates.map(function (d) { return { id: card.vidByDate[d] }; });
    runChain(function () { return postAdd(items, buildDancerProp(name)); }, function () {
      dates.forEach(function (d) { locAdd(card, d, name); });
    });
  }

  // remove `name` from EVERY date (pressed-chip unpress card-wide).
  function removeDancerCardWide(card, name) {
    runSpecs(specsForName(card, name, card.dates));
  }

  // remove `name` from ONE date (row chip confirm).
  function removeDancerFromDate(card, name, when) {
    runSpecs(specsForName(card, name, [when]));
  }

  // add `name` to ONE date only (row "+" pick).
  function addDancerToDate(card, name, when) {
    rememberName(name);
    if (!card.vidByDate[when]) return;
    runChain(function () { return postAdd([{ id: card.vidByDate[when] }], buildDancerProp(name)); }, function () {
      locAdd(card, when, name);
    });
  }

  // remove a whole DATE for everyone (row ×) — ONE update.js.
  function removeDate(card, when) {
    var keys = keysOfDate(card, when);
    if (!keys.length) return;
    runChain(function () { return postUpdate(zeroMap(keys)); }, function () { locRemove(keys); });
  }

  // remove the whole CLASS (card) — ONE update.js for every line.
  function removeClass(card) {
    var keys = [];
    card.dates.forEach(function (d) { keys = keys.concat(keysOfDate(card, d)); });
    if (!keys.length) return;
    runChain(function () { return postUpdate(zeroMap(keys)); }, function () { locRemove(keys); });
  }

  // delete a saved NAME entirely — cascade the per-date rule across all cards,
  // then drop from localStorage. (sole-holder → blank; else remove line.)
  function deleteName(name) {
    forgetName(name);
    NAME_CONFIRM = null;
    var specs = [];
    cards.forEach(function (card) {
      specs = specs.concat(specsForName(card, name, card.dates));
    });
    if (!specs.length) { render(lastCart); return; }
    runSpecs(specs);
  }

  // ============================================================
  //  PER-CARD WIRING.
  // ============================================================
  function wireCard(card) {
    var el = body.querySelector('[data-card="' + cssAttr(card.cls) + '"]');
    if (!el) return;

    // expand / collapse (settled card chevron)
    el.querySelectorAll('[data-expand]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTransient();
        ui[card.cls] = ui[card.cls] || {};
        ui[card.cls].expanded = !ui[card.cls].expanded;
        render(lastCart);
      });
    });

    // fine-tune / peek toggle (sub-toggle on unassigned, finetune on expanded)
    el.querySelectorAll('[data-toggle-finetune]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTransient();
        ui[card.cls] = ui[card.cls] || {};
        ui[card.cls].revealed = !ui[card.cls].revealed;
        render(lastCart);
      });
    });

    // who-panel Edit/Done
    el.querySelectorAll('[data-who-edit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTransient();
        ui[card.cls] = ui[card.cls] || {};
        ui[card.cls].editWho = !ui[card.cls].editWho;
        render(lastCart);
      });
    });
    el.querySelectorAll('[data-del-name]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var name = btn.getAttribute('data-del-name');
        var uses = nameUses(cards, name);
        if (uses === 0) { deleteName(name); return; }
        clearTransient();
        NAME_CONFIRM = { name: name, cls: card.cls, uses: uses };
        render(lastCart);
      });
    });
    el.querySelectorAll('[data-name-go]').forEach(function (btn) {
      btn.addEventListener('click', function () { if (NAME_CONFIRM) deleteName(NAME_CONFIRM.name); });
    });
    el.querySelectorAll('[data-name-no]').forEach(function (btn) {
      btn.addEventListener('click', function () { NAME_CONFIRM = null; render(lastCart); });
    });

    // tri-state pick chips (assign / add card-wide / remove card-wide)
    el.querySelectorAll('[data-pick]').forEach(function (chip) {
      var fire = function () {
        var name = chip.getAttribute('data-name');
        var state = chip.getAttribute('aria-pressed');   // 'true' | 'mixed' | 'false'
        if (state === 'true') {
          // fully on → unpress removes them from every date — confirm first
          clearTransient();
          CARDWIDE_CONFIRM = { cls: card.cls, name: name };
          render(lastCart);
        } else if (isUnassigned(card)) {
          // first assignment on a fully-blank card → fill blanks via change.js
          clearTransient();
          assignAllBlank(card, name);
        } else {
          // off or mixed on a settled card → add to the dates they're missing
          clearTransient();
          addDancerCardWide(card, name);
        }
      };
      chip.addEventListener('click', fire);
      chip.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire(); }
      });
    });

    // "+ Add dancer" inline name input (panel-level)
    el.querySelectorAll('[data-who] [data-addopen]').forEach(function (chip) {
      attachAddInput(chip, function (name) {
        if (isUnassigned(card)) assignAllBlank(card, name);
        else addDancerCardWide(card, name);
      });
    });

    // row "+" → scoped picker
    el.querySelectorAll('[data-rowadd]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTransient();
        ROW_PICK = { cls: card.cls, date: btn.getAttribute('data-date') };
        render(lastCart);
      });
    });
    el.querySelectorAll('[data-rowpickname]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var d = chip.getAttribute('data-date'), name = chip.getAttribute('data-name');
        ROW_PICK = null; addDancerToDate(card, name, d);
      });
    });
    el.querySelectorAll('[data-rowpicknew]').forEach(function (chip) {
      var d = chip.getAttribute('data-date');
      attachAddInput(chip, function (name) {
        ROW_PICK = null; addDancerToDate(card, name, d);
      }, function () { ROW_PICK = null; render(lastCart); });
    });
    el.querySelectorAll('[data-rowpickcancel]').forEach(function (btn) {
      btn.addEventListener('click', function () { ROW_PICK = null; render(lastCart); });
    });

    // row chip → remove-from-one-date confirm
    el.querySelectorAll('[data-rowchip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        clearTransient();
        ROW_CONFIRM = { cls: card.cls, date: chip.getAttribute('data-date'), name: chip.getAttribute('data-name') };
        render(lastCart);
      });
    });
    el.querySelectorAll('[data-rowgo]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!ROW_CONFIRM) return;
        var c = cardForCls(ROW_CONFIRM.cls);
        var d = ROW_CONFIRM.date, name = ROW_CONFIRM.name;
        ROW_CONFIRM = null;
        if (c) removeDancerFromDate(c, name, d);
      });
    });
    el.querySelectorAll('[data-rowno]').forEach(function (btn) {
      btn.addEventListener('click', function () { ROW_CONFIRM = null; render(lastCart); });
    });

    // per-date × — confirm before removing that date for everyone
    el.querySelectorAll('[data-removedate]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTransient();
        DATE_CONFIRM = { cls: card.cls, date: btn.getAttribute('data-date') };
        render(lastCart);
      });
    });
    el.querySelectorAll('[data-datego]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = btn.getAttribute('data-date'); DATE_CONFIRM = null; removeDate(card, d);
      });
    });
    el.querySelectorAll('[data-dateno]').forEach(function (btn) {
      btn.addEventListener('click', function () { DATE_CONFIRM = null; render(lastCart); });
    });

    // card-wide dancer unpress — confirm go / keep
    el.querySelectorAll('[data-cardwide-go]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var name = btn.getAttribute('data-name'); CARDWIDE_CONFIRM = null; removeDancerCardWide(card, name);
      });
    });
    el.querySelectorAll('[data-cardwide-no]').forEach(function (btn) {
      btn.addEventListener('click', function () { CARDWIDE_CONFIRM = null; render(lastCart); });
    });

    // remove whole class — confirm before removing the card
    el.querySelectorAll('[data-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTransient();
        REMOVE_CONFIRM = { cls: card.cls };
        render(lastCart);
      });
    });
    el.querySelectorAll('[data-remove-go]').forEach(function (btn) {
      btn.addEventListener('click', function () { REMOVE_CONFIRM = null; removeClass(card); });
    });
    el.querySelectorAll('[data-remove-no]').forEach(function (btn) {
      btn.addEventListener('click', function () { REMOVE_CONFIRM = null; render(lastCart); });
    });
  }
  function cssAttr(s) { return String(s).replace(/"/g, '\\"'); }

  // inline add-name input + visible "Add" commit chip, with the double-commit
  // guard (Enter / "done" / Add tap / blur all commit once; Escape cancels).
  // Tapping Add blurs the input first, so blur's commit usually wins — the
  // click handler is the keyboard/AT path and the guard makes it idempotent.
  function attachAddInput(chip, onName, onCancel) {
    chip.addEventListener('click', function () {
      var wrap = document.createElement('span');
      wrap.className = 'dchip-entry';
      var input = document.createElement('input');
      input.className = 'dchip-input'; input.type = 'text'; input.placeholder = 'New name…';
      input.setAttribute('autocomplete', 'off'); input.setAttribute('maxlength', '40');
      // mobile keyboards: return key reads "done", names capitalize, no autocorrect
      input.setAttribute('enterkeyhint', 'done');
      input.setAttribute('autocapitalize', 'words');
      input.setAttribute('autocorrect', 'off');
      var go = document.createElement('button');
      go.type = 'button'; go.className = 'dchip-go'; go.textContent = 'Add';
      wrap.appendChild(input); wrap.appendChild(go);
      chip.replaceWith(wrap); input.focus();
      var committed = false;
      function commit() {
        if (committed) return; committed = true;
        var name = input.value.trim();
        if (name) onName(name);
        else { if (onCancel) onCancel(); else render(lastCart); }
      }
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { committed = true; if (onCancel) onCancel(); else render(lastCart); }
      });
      go.addEventListener('click', commit);
      input.addEventListener('blur', commit);
    });
  }

  // ============================================================
  //  FOOTER wiring (per render).
  // ============================================================
  function wireFoot() {
    // snapshot the host's elements — these closures fire later (waiver change),
    // after the active pointers have moved to another host or another render.
    var fEl = foot, bEl = body;
    // WAIVER TOGGLE — strip tap expands, collapse-header tap collapses. Both flip
    // the shared waiverExpanded flag (so both hosts move in step) and re-render.
    // Per render only one of [strip | collapse-header] is present (and only while
    // a card is still unassigned — once all assigned the card is the final state
    // with neither affordance), so these are no-ops when the other state shows.
    var strip = fEl.querySelector('[data-waiver-strip]');
    if (strip) strip.addEventListener('click', function () {
      waiverExpanded = true;
      render(lastCart);
    });
    var collapse = fEl.querySelector('[data-waiver-collapse]');
    if (collapse) collapse.addEventListener('click', function () {
      waiverExpanded = false;
      render(lastCart);
    });
    var readlink = fEl.querySelector('[data-readwaiver]');
    if (readlink) readlink.addEventListener('click', function (e) {
      e.stopPropagation();
      var box = fEl.querySelector('[data-waivertext]');
      if (!box) return;
      var open = box.getAttribute('data-open') === 'true';
      box.setAttribute('data-open', String(!open));
      readlink.textContent = open ? 'Read full waiver' : 'Hide waiver';
    });
    var waiver = fEl.querySelector('[data-waiver]');
    var waiverCard = fEl.querySelector('[data-waiver-card]');
    function applyWaiver() {
      if (waiverCard) waiverCard.classList.toggle('is-agreed', waiver.checked);
      // Mirror the agreement into the local cart model so a re-render BEFORE the
      // /cart/update.js response settles (e.g. check the box, then collapse the
      // waiver) restores the checked box + agreed tint from lastCart instead of the
      // stale pre-check attribute. The async write below persists it server-side;
      // this keeps the optimistic render honest. (renderFoot reads `agreed` off
      // cart.attributes['Waiver Agreement']; the collapsed strip's agreed signal
      // reads the same.)
      if (lastCart) {
        lastCart.attributes = lastCart.attributes || {};
        lastCart.attributes['Waiver Agreement'] = waiver.checked ? 'Agreed ' + new Date().toISOString() : '';
      }
      setWaiverAttribute(waiver.checked);
      // both surfaces share the "agreed" attribute — re-gate every host so the
      // page and drawer never disagree about whether checkout is unlocked.
      hosts.forEach(function (h) {
        var ow = h.footEl && h.footEl.querySelector('[data-waiver]');
        if (ow && ow !== waiver) ow.checked = waiver.checked;
        if (h.footEl) {
          var oc = h.footEl.querySelector('[data-waiver-card]');
          if (oc) oc.classList.toggle('is-agreed', waiver.checked);
        }
        refreshGate(h.bodyEl, h.footEl);
      });
    }
    if (waiver) waiver.addEventListener('change', applyWaiver);
    // the agree text toggles the box; the inline "Read full waiver" link sits
    // OUTSIDE that label so clicking it can't toggle the checkbox. The label has
    // no `for` (won't natively toggle a sibling), so we flip it explicitly — same
    // as the locked prototype.
    var label = fEl.querySelector('[data-waiver-label]');
    if (label && waiver) label.addEventListener('click', function () {
      waiver.checked = !waiver.checked;
      applyWaiver();
    });
    var checkout = fEl.querySelector('[data-checkout]');
    if (checkout) checkout.addEventListener('click', function () {
      if (!checkout.hasAttribute('disabled')) window.location.href = '/checkout';
    });
    refreshGate(bEl, fEl);
  }

  // ---- open / close + focus trap (drawer host only) --------------
  // P2-3: filter out [hidden] elements — a hidden control (e.g. between
  // renders) shouldn't be a Tab-trap boundary.
  function focusable() {
    return Array.prototype.filter.call(
      drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.hidden; }
    );
  }
  function open() {
    if (!drawer || !scrim) return;   // page-only context: no drawer to open
    // re-read the saved-name roster (registration redesign, 2026-07-14): the
    // PDP finalize sheet can add a brand-new name to localStorage (cs-dancers)
    // moments before calling this via window.CartDrawer.open() — KNOWN was
    // otherwise only loaded once at script init, so a same-visit add would
    // render its dancer on the card (toRegs/dancersOnCard fall back to any
    // cart name not in KNOWN) but miss the picker-chip / KNOWN-first ordering
    // until a full page reload. Cheap (one JSON.parse), safe to do every open.
    KNOWN = loadKnown();
    lastFocus = document.activeElement;
    scrim.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-locked');
    refresh().then(function () {
      var closeBtn = drawer.querySelector('[data-cart-close]');
      if (closeBtn) closeBtn.focus();
    });
  }
  function close() {
    if (!drawer || !scrim) return;
    scrim.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-locked');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // Delegated so a customizer-reloaded header button opens the drawer with no
  // re-wiring (the drawer itself lives in layout/theme.liquid — never reloads).
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-cart-open]');
    if (!b) return;
    e.preventDefault();
    open();
  });
  if (drawer && scrim) {
    drawer.querySelectorAll('[data-cart-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var f = focusable();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  // Expose the drawer open() so the PDP date-picker can pop the cart after an
  // AJAX add (window.CartDrawer already carries moneyFormat/schedules).
  window.CartDrawer = window.CartDrawer || {};
  window.CartDrawer.open = open;

  // ---- intercept product add-to-cart → add via AJAX + open drawer
  document.querySelectorAll('form.product-form, form[action$="/cart/add"]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form)
      }).then(function (r) { return r.json(); }).then(function () {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
        open();
      }).catch(function () {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
        form.submit();
      });
    });
  });

  // initial badge sync
  fetchCart().then(function (cart) { setCount(cart.item_count); });

  // The /cart page host renders on load (the drawer renders lazily on open).
  // Replaces the no-JS Liquid fallback inside #cart-page with the live V3 cards.
  if (pageRoot) { refresh(); }

  // badge resync on external adds (PDP multi-date picker). When a page host is
  // mounted, also re-render it so an add from another tab/PDP reflects live.
  document.addEventListener('cart:refresh', function () {
    if (pageRoot) { refresh(); }
    else { fetchCart().then(function (cart) { setCount(cart.item_count); }); }
  });
})();
